import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Invoice } from "../../../SharedModule/types/shared-module-types";
import {
  LAST_MONTHS_END,
  LAST_MONTHS_START,
} from "../../../SharedModule/utils/dateUtils";
import { sortDataBy } from "../../../SharedModule/utils/dataSort";

type ClientInvoicesState = {
  isLoadingClientInvoices: boolean;
  isLoadedClientInvoices: boolean;
  clientInvoicesInfo: Invoice[];
  clientInvoicesfilters: {
    dateFrom: string;
    dateTo: string;
    accountIds: number[];
    statusIds: [3]; // only exported
  };
  orderedBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string" | "date";
  };
};

export const initialState: ClientInvoicesState = {
  isLoadingClientInvoices: true,
  isLoadedClientInvoices: false,
  clientInvoicesInfo: [],
  clientInvoicesfilters: {
    dateFrom: LAST_MONTHS_START.toISO({ includeOffset: false }),
    dateTo: LAST_MONTHS_END.toISO({ includeOffset: false }),
    accountIds: [],
    statusIds: [3],
  },
  orderedBy: {
    orderBy: "id",
    criteria: "asc",
  },
};

export const clientInvoicesSlice = createSlice({
  name: "clientInvoices",
  initialState,
  reducers: {
    loadClientInvoices: (state, action) => {
      state.clientInvoicesInfo = action.payload;
      state.isLoadingClientInvoices = false;
      state.isLoadedClientInvoices = true;
    },
    setIsLoadingClientInvoices: (state, action) => {
      state.isLoadingClientInvoices = action.payload;
    },
    setClientInvoicesFilters: (state, action) => {
      state.clientInvoicesfilters = action.payload;
    },
    changeOrder: (
      state,
      action: PayloadAction<{
        orderBy: string;
        dataType?: "string" | "date";
      }>
    ) => {
      const newOrderedBy: any = {
        ...action.payload,
        criteria: "asc",
      };
      if (newOrderedBy.orderBy === state.orderedBy.orderBy) {
        newOrderedBy.criteria =
          state.orderedBy.criteria === "asc" ? "desc" : "asc";
      }
      return { ...state, orderedBy: newOrderedBy };
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  loadClientInvoices,
  setIsLoadingClientInvoices,
  setClientInvoicesFilters,
  changeOrder,
} = clientInvoicesSlice.actions;

export const IsLoadingClientInvoices = (state: any) => {
  return state.clientInvoices.isLoadingClientInvoices;
};

export const IsLoadedClientInvoices = (state: any) => {
  return state.clientInvoices.isLoadedClientInvoices;
};

export const SelectClientInvoicesInfo = (state: any) => {
  return state.clientInvoices.clientInvoicesInfo;
};

export const SelectClientInvoicesFilters = (state: any) => {
  return state.clientInvoices.clientInvoicesfilters;
};

export const SelectInvoices = ({
  clientInvoices,
}: {
  clientInvoices: ClientInvoicesState;
}) => {
  return { list: clientInvoices.clientInvoicesInfo };
};

export const SelectOrderCriteria = ({
  clientInvoices,
}: {
  clientInvoices: ClientInvoicesState;
}) => clientInvoices.orderedBy;

export const SelectOrderedBy = createSelector(
  [SelectInvoices, SelectOrderCriteria],
  (clientInvoices, orderCriteria) => {
    return {
      list:
        (clientInvoices.list &&
          sortDataBy(
            clientInvoices.list,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        null,
    };
  }
);

export default clientInvoicesSlice.reducer;
